import { isPlatformBrowser, Location } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthenticationService } from '@common-modules/shared/auth/services/authentication.service';
import { SettingsService } from '@common-modules/shared/config/settings.service';
import { IconLoaderService } from '@common-modules/shared/icon-loader.service';
import { LocalizationHelperService } from '@common-modules/shared/localization/localization-helper.service';
import { LogService } from '@common-modules/shared/wlm-log/log.service';
import { SpinnerService } from '@common-modules/wlm-spinner/spinner.service';
import { ReplaySubject } from 'rxjs';
import { CtInitialUserService } from './cloud-twin-shared/core/ct-initial-user.service';

const detachedLayoutConfig = 'gl-window-config';

/**
 * Encalsulates the logic of starting the application by fetching remote settings
 * and launching authentication, between other things.
 */

@Injectable({ providedIn: 'root' })
export class StartupService {
  private _configLoaded$ = new ReplaySubject<boolean>(1);
  private _authSuccess$ = new ReplaySubject<boolean>(1);

  readonly configLoaded$ = this._configLoaded$.asObservable();
  readonly authSuccess$ = this._authSuccess$.asObservable();

  private setLoading: (loading: boolean) => void;
  private readonly _startupMock = false;
  private _isDetachedLayout = false;

  constructor(
    @Inject(PLATFORM_ID) private readonly _platformId: object,
    private readonly _iconLoaderService: IconLoaderService,
    private readonly _settingsService: SettingsService,
    private readonly _localization: LocalizationHelperService,
    private readonly _authenticationService: AuthenticationService,
    private readonly _logService: LogService,
    private readonly _spinnerService: SpinnerService,
    private readonly _router: Router,
    private readonly _location: Location,
    private readonly _route: ActivatedRoute,
    private readonly _ctInitialUserService: CtInitialUserService
  ) {
    this.setLoading = this._spinnerService.buildSetLoadingFn('STARTUP', {
      overlayType: 'logo',
    });

    this.checkDetachedLayout();
  }

  startup(): void {
    if (this._startupMock) {
      this.startupMock();
      return;
    }
    this._localization.config();

    this.setLoading(true);
    this._settingsService.ready$.subscribe({
      error: (error) => {
        this._configLoaded$.next(false);
        this._logService.error({ msg: 'Error retrieving main settings', payload: error });
        this.setLoading(false);
      },
      next: () => {
        this.configureOAuth();
        this._configLoaded$.next(true);
        this._authenticationService.accessToken$.subscribe({
          error: (error) => {
            this._authSuccess$.next(false);
            this._logService.error({ msg: 'Error retrieving token', payload: error });
            this.setLoading(false);
          },
          next: (x) => {
            this._ctInitialUserService.ensureIntegrationSetup(() => {
              this._authSuccess$.next(true);
              this.restorePreviousNavigation();
              this.setLoading(false);
            });
          },
        });
      },
    });

    this._iconLoaderService.registerAllIcons();
  }

  /**
   * Starts application without fetching settings or launching tokens
   */
  private startupMock(): void {
    this._localization.config();
    this._iconLoaderService.registerAllIcons();
    this._settingsService.mockSettings({});

    this._configLoaded$.next(true);
    this._authSuccess$.next(true);
  }

  private configureOAuth(): void {
    if (isPlatformBrowser(this._platformId)) {
      this._authenticationService.customLoadDiscoveryDocumentAndTryLogin();
    }
  }

  private restorePreviousNavigation(): void {
    if (this._isDetachedLayout) {
      return;
    }

    let urlPath = this._location.path();
    if (this.isOauthCallbackRoute(urlPath)) {
      urlPath = '/';
    }

    this._router.navigate([urlPath]);
  }

  private isOauthCallbackRoute(path: string): boolean {
    const params = this._route.snapshot.queryParamMap;
    const noParamsRoute = path.split('?')[0];
    return (
      (noParamsRoute === '' || noParamsRoute === '/') && params.has('code') && params.has('state')
    );
  }

  private checkDetachedLayout(): void {
    const urlPath = this._location.path();
    this._isDetachedLayout = urlPath.includes(detachedLayoutConfig);
  }
}
